import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import PhoneAppsGate, { welcomeAppsHref } from './components/auth/PhoneAppsGate';
import { ProtectedRoute, settleExistingAccount } from './components/auth/AppGate';
import { accountNeedsSetup } from './lib/accountSetup';
import { ThemeProvider } from './lib/theme';
import { ContactsProvider } from './lib/ContactsContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TourProvider, useTour } from './components/tour/TourContext';
import InteractiveTour from './components/tour/InteractiveTour';
import React, { Suspense, useEffect } from 'react';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import LinkRequiredPage from './pages/LinkRequiredPage';
import VerifyToLinkPage from './pages/VerifyToLinkPage';
import SignupPage from './pages/SignupPage';
import NotFoundPage from './pages/NotFoundPage';
// SearchPage is eager-loaded (not React.lazy) on purpose: lazy-loading
// puts a Suspense fallback between the user's tap on the Search nav icon
// and the SearchPage mounting, which breaks iOS Safari's user-gesture
// chain. The result was that the search input would gain caret focus but
// the keyboard wouldn't open, forcing users to tap a second time. Keeping
// SearchPage in the main bundle is a small cost (~4KB gz) for instant
// keyboard activation.
import SearchPage from './pages/SearchPage';
import './App.css';

// Lazy load heavy pages for code splitting
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ContactDetailPage = React.lazy(() => import('./pages/ContactDetailPage'));
const GroupsPage = React.lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = React.lazy(() => import('./pages/GroupDetailPage'));
const PlacesPage = React.lazy(() => import('./pages/PlacesPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const AppearancePage = React.lazy(() => import('./pages/AppearancePage'));
const UpdatesPage = React.lazy(() => import('./pages/UpdatesPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const AdminPortalPage = React.lazy(() => import('./pages/AdminPortalPage'));
// `/adminportal/invite/:token` routes to the named export from the same
// module — wrap it in a default-export shim so React.lazy can consume it.
const AdminInviteAcceptPage = React.lazy(() =>
  import('./pages/AdminPortalPage').then((m) => ({ default: m.AdminInviteAcceptPage })),
);
const ImportAddPage = React.lazy(() => import('./pages/ImportAddPage'));
const ManageContactsHubPage = React.lazy(() => import('./pages/ManageContactsHubPage'));
const ManageListPage = React.lazy(() => import('./pages/ManageListPage'));
const HomeCitiesExplainPage = React.lazy(() => import('./pages/HomeCitiesExplainPage'));
const NameCleanupExplainPage = React.lazy(() => import('./pages/NameCleanupExplainPage'));
const LegacyAddContactsRedirect = React.lazy(() => import('./pages/LegacyAddContactsRedirect'));
const NewContactPage = React.lazy(() => import('./pages/NewContactPage'));
const FavoritesPage = React.lazy(() => import('./pages/FavoritesPage'));
const RecentlyAddedPage = React.lazy(() => import('./pages/RecentlyAddedPage'));
const TwoFactorAuthPage = React.lazy(() => import('./pages/TwoFactorAuthPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const DataPrivacyPage = React.lazy(() => import('./pages/DataPrivacyPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const UserAgreementPage = React.lazy(() => import('./pages/UserAgreementPage'));
const SharedContactPage = React.lazy(() => import('./pages/SharedContactPage'));
const PublicProfilePage = React.lazy(() => import('./pages/PublicProfilePage'));
const IntroLandingPage = React.lazy(() => import('./pages/IntroLandingPage'));
const MyProfileSharingPage = React.lazy(() => import('./pages/MySharingPermissionsPage'));
const SharePage = React.lazy(() => import('./pages/SharePage'));
const SearchUsersPage = React.lazy(() => import('./pages/SearchUsersPage'));
const ShareSubmitPage = React.lazy(() => import('./pages/ShareSubmitPage'));
const QuickCaptureSettingsPage = React.lazy(() => import('./pages/QuickCaptureSettingsPage'));
const IntakeQueuePage = React.lazy(() => import('./pages/IntakeQueuePage'));
const ReviewSharedContactPage = React.lazy(() => import('./pages/ReviewSharedContactPage'));
const IncomingSharePage = React.lazy(() => import('./pages/IncomingSharePage'));
const GroupShareLandingPage = React.lazy(() => import('./pages/GroupShareLandingPage'));
const InviteLandingPage = React.lazy(() => import('./pages/InviteLandingPage'));
const GroupShareHostPage = React.lazy(() => import('./pages/GroupShareHostPage'));
const ConsentRequestsPage = React.lazy(() => import('./pages/ConsentRequestsPage'));
const DiscoveryPage = React.lazy(() => import('./pages/DiscoveryPage'));
const MagicLinkLandingPage = React.lazy(() => import('./pages/MagicLinkLandingPage'));
const MyEmailsPage = React.lazy(() => import('./pages/MyEmailsPage'));
const EmailVerifyLandingPage = React.lazy(() => import('./pages/EmailVerifyLandingPage'));
const ResetLinkLandingPage = React.lazy(() => import('./pages/ResetLinkLandingPage'));
const PhoneAppsPromptPage = React.lazy(() => import('./pages/PhoneAppsPromptPage'));
// Session 126u — Web Share Target landing page. Receives shared
// screenshots from Android's system Share menu (via the service
// worker in public/share-target-sw.js) and uploads them through the
// intake pipeline. Also handles the `?next=/share-target` bounce
// from /login when an un-authed user tries to share.
const ShareTargetPage = React.lazy(() => import('./pages/ShareTargetPage'));

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="w-7 h-7 border-2 border-nav-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function LazyPage({ children }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function OpenContactRedirect() {
  const { id } = useParams();
  const safe = String(id || '');
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(safe)) return <Navigate to="/" replace />;
  return <Navigate to={`/contacts/${safe}`} replace />;
}

function AuthRoute({ children }) {
  const { user, loading, refreshUser } = useAuth();
  const location = useLocation();
  useEffect(() => {
    settleExistingAccount(user, refreshUser);
  }, [user, refreshUser]);
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-nav-blue border-t-transparent rounded-full animate-spin" /></div>;
  const welcome = welcomeAppsHref(location.pathname, location.search, user);
  if (welcome) return <Navigate to={welcome} replace />;
  if (user && !accountNeedsSetup(user)) return <Navigate to="/" />;
  if (user && accountNeedsSetup(user)) return <Navigate to="/onboarding" />;
  return children;
}

function OnboardRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-nav-blue border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" />;
  // Session 126 — allow re-entry to the wizard so the Home progress bar
  // deep-links (home_location / terms / email) can drop the user back
  // into the right step. The wizard itself auto-skips past completed
  // steps and bounces to / when everything in the 3-step gate is done.
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/auth/magic" element={<LazyPage><MagicLinkLandingPage /></LazyPage>} />
      <Route path="/auth/link-required" element={<LinkRequiredPage />} />
      <Route path="/auth/verify-to-link" element={<VerifyToLinkPage />} />
      <Route path="/signup" element={<AuthRoute><SignupPage /></AuthRoute>} />
      <Route path="/welcome/apps" element={<LazyPage><PhoneAppsPromptPage /></LazyPage>} />
      <Route path="/onboarding" element={<OnboardRoute><LazyPage><OnboardingPage /></LazyPage></OnboardRoute>} />
      <Route path="/adminportal" element={<LazyPage><AdminPortalPage /></LazyPage>} />
      <Route path="/adminportal/invite/:token" element={<LazyPage><AdminInviteAcceptPage /></LazyPage>} />
      <Route path="/shared/:token" element={<LazyPage><SharedContactPage /></LazyPage>} />
      <Route path="/p/:token" element={<LazyPage><PublicProfilePage /></LazyPage>} />
      <Route path="/intro/:token" element={<LazyPage><IntroLandingPage /></LazyPage>} />
      <Route path="/s/:token" element={<LazyPage><ShareSubmitPage /></LazyPage>} />
      {/* Group Share session (session 111) — `/session/:token` is the
          PUBLIC landing page that anyone (PiK user OR guest) can hit by
          scanning the host's QR. `/session/host/:token` is the owner-only
          live host dashboard, sits inside ProtectedRoute below. */}
      <Route path="/session/:token" element={<LazyPage><GroupShareLandingPage /></LazyPage>} />
      {/* Session 242 — recipient-facing invite landing. Public, no auth. */}
      <Route path="/invite/:token" element={<LazyPage><InviteLandingPage /></LazyPage>} />
      {/* Public landing pages (no auth required) — verification + password reset links. */}
      <Route path="/verify-email/:token" element={<LazyPage><EmailVerifyLandingPage /></LazyPage>} />
      <Route path="/reset/:token" element={<LazyPage><ResetLinkLandingPage /></LazyPage>} />
      {/* Legal pages — must be reachable from the onboarding wizard (which
          opens them in a new tab) AND from Settings. Routed both inside
          ProtectedRoute (with AppShell) and at the top level (raw page). */}
      <Route path="/legal/privacy-policy" element={<LazyPage><PrivacyPolicyPage /></LazyPage>} />
      <Route path="/legal/user-agreement" element={<LazyPage><UserAgreementPage /></LazyPage>} />
      <Route element={<ProtectedRoute><ContactsProvider><AppShell /></ContactsProvider></ProtectedRoute>}>
        <Route path="/" element={<LazyPage><HomePage /></LazyPage>} />
        <Route path="/share" element={<LazyPage><SharePage /></LazyPage>} />
        <Route path="/intros" element={<LazyPage><SharePage /></LazyPage>} />
        <Route path="/session/host/:token" element={<LazyPage><GroupShareHostPage /></LazyPage>} />
        <Route path="/contacts/:id" element={<LazyPage><ContactDetailPage /></LazyPage>} />
        <Route path="/open/contact/:id" element={<OpenContactRedirect />} />
        <Route path="/groups" element={<LazyPage><GroupsPage /></LazyPage>} />
        <Route path="/groups/:id" element={<LazyPage><GroupDetailPage /></LazyPage>} />
        <Route path="/places" element={<LazyPage><PlacesPage /></LazyPage>} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/search-users" element={<LazyPage><SearchUsersPage /></LazyPage>} />
        <Route path="/favorites" element={<LazyPage><FavoritesPage /></LazyPage>} />
        <Route path="/recently-added" element={<LazyPage><RecentlyAddedPage /></LazyPage>} />
        <Route path="/settings" element={<LazyPage><SettingsPage /></LazyPage>} />
        <Route path="/appearance" element={<LazyPage><AppearancePage /></LazyPage>} />
        <Route path="/settings/profile-sharing" element={<LazyPage><MyProfileSharingPage /></LazyPage>} />
        <Route path="/settings/my-emails" element={<LazyPage><MyEmailsPage /></LazyPage>} />
        <Route path="/settings/quick-capture" element={<LazyPage><QuickCaptureSettingsPage /></LazyPage>} />
        <Route path="/settings/intake-queue" element={<LazyPage><IntakeQueuePage /></LazyPage>} />
        <Route path="/intake/review/:queueId" element={<LazyPage><ReviewSharedContactPage /></LazyPage>} />
        <Route path="/incoming-share/:id" element={<LazyPage><IncomingSharePage /></LazyPage>} />
        <Route path="/share-target" element={<LazyPage><ShareTargetPage /></LazyPage>} />
        <Route path="/consents" element={<LazyPage><ConsentRequestsPage /></LazyPage>} />
        <Route path="/discovery" element={<LazyPage><DiscoveryPage /></LazyPage>} />
        <Route path="/settings/2fa" element={<LazyPage><TwoFactorAuthPage /></LazyPage>} />
        <Route path="/settings/security" element={<LazyPage><SecurityPage /></LazyPage>} />
        <Route path="/settings/data-privacy" element={<LazyPage><DataPrivacyPage /></LazyPage>} />
        <Route path="/settings/privacy-policy" element={<LazyPage><PrivacyPolicyPage /></LazyPage>} />
        <Route path="/settings/user-agreement" element={<LazyPage><UserAgreementPage /></LazyPage>} />
        <Route path="/updates" element={<LazyPage><UpdatesPage /></LazyPage>} />
        <Route path="/admin" element={<LazyPage><AdminPage /></LazyPage>} />
        <Route path="/import" element={<LazyPage><LegacyAddContactsRedirect /></LazyPage>} />
        <Route path="/add-contacts" element={<LazyPage><LegacyAddContactsRedirect /></LazyPage>} />
        <Route path="/import-add" element={<LazyPage><ImportAddPage /></LazyPage>} />
        <Route path="/manage-contacts" element={<LazyPage><ManageContactsHubPage /></LazyPage>} />
        <Route path="/manage-contacts/list" element={<LazyPage><ManageListPage /></LazyPage>} />
        <Route path="/manage-contacts/home-cities" element={<LazyPage><HomeCitiesExplainPage /></LazyPage>} />
        <Route path="/manage-contacts/name-cleanup" element={<LazyPage><NameCleanupExplainPage /></LazyPage>} />
        <Route path="/add" element={<LazyPage><NewContactPage /></LazyPage>}/>
        <Route path="/new-contact" element={<LazyPage><NewContactPage /></LazyPage>}/>
        <Route path="/contacts/new" element={<LazyPage><NewContactPage /></LazyPage>}/>
      </Route>
      {/* Session 126t — real 404 page instead of the silent
          Navigate-to-home anti-pattern. This also catches in-app
          links that point to deleted routes during development. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <TourProvider>
              <AppRoutes />
              <PhoneAppsGate />
              <AppLevelTour />
              <Toaster position="top-center" richColors />
            </TourProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Session 126q — the InteractiveTour MUST live at App-shell level
 * (not inside any page-scoped component) so that route transitions
 * triggered by the tour itself don't unmount the spotlight overlay.
 * The OnboardingProgressTracker's "Take a tour" button only calls
 * `openTour()` from TourContext; the actual rendering lives here.
 */
function AppLevelTour() {
  const { tourOpen, closeTour } = useTour();
  return tourOpen ? <InteractiveTour open={tourOpen} onClose={closeTour} /> : null;
}
